/**
 * デザイナー貢献度アンケートの回答モーダル。
 *
 * 1 モーダル = 1 子チケット。同じ案件に未回答が複数あるときはヘッダに進捗（1/3 件）を出し、
 * 送信後そのまま次の子チケットへ進む。PL が溜めた分をまとめて消化できることが回答率に直結する。
 *
 * 設問は旧 Google Form と 1:1。選択肢の長い説明はセクション見出しの直下に 1 回だけ置く
 * （旧 Form は設問ごとに同じ説明を 5 回繰り返していて読まれていない）。
 *
 * 配色はセクション単位で地色を変え（解像度=青緑 / 貢献度=桃）、貢献度の 3 軸は
 * **ID 看板「チーム貢献度評価」と同じ軸色**を使う。回答画面と看板で同じ軸が違う色だと
 * 同一の軸だと認識されないため、色は看板側の SSOT から引く。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business デザイナー貢献度アンケートの回答モーダル（解像度5軸 + 貢献度3軸 + Good/More）
 * @graph-connects useSubmitEvaluationAnswer [calls] アンケート回答登録
 */
import { useState } from 'react';

import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';

import { useSubmitEvaluationAnswer } from '../../../hooks/useEvaluationSurvey.js';
import {
  CONTRIBUTION_AXES,
  CONTRIBUTION_VALUES,
  RESOLUTION_AXES,
  RESOLUTION_VALUES,
  type ContributionAnswer,
  type ContributionValue,
  type EvaluationTask,
  type ResolutionAnswer,
  type ResolutionValue,
} from '../../../types/idt-design-evaluation.js';
import {
  EVALUATION_AXES,
  EVALUATION_RADAR_COLOR,
  RESOLUTION_RADAR_COLOR,
  type EvaluationAxisKey,
} from '../idt-kpi/designEvaluation.js';
import { SurveyScaleBar } from './SurveyScaleBar.js';

interface EvaluationSurveyModalProps {
  /** 未回答タスク（この案件ぶん）。先頭から順に回答していく */
  tasks: EvaluationTask[];
  open: boolean;
  onClose: () => void;
}

/**
 * 選択肢ごとの説明。**配列で持つ**のが要点。
 *
 * オブジェクトリテラルにすると JS のキー順序規則（整数様キーが数値順で先、その後に文字列キーが
 * 挿入順）で `1〜5` が `対象外` より前に並び、**ボタンの並び（対象外 が先頭）と凡例の並びが
 * 食い違う**。選択肢の並びが 2 箇所で違うと読み手が対応を取り違えるため、
 * ボタンと同じ順序を型で担保する。
 * @graph-connects none
 */
const RESOLUTION_LEGEND: readonly (readonly [ResolutionValue, string])[] = [
  ['低', '具体的なHowについてはあまり決まっていない状態'],
  ['中', 'PL側でHow案が整理されている状態'],
  ['高', 'デザイナーが入る余地がないレベルで、Howが決まっている状態'],
];

/** 貢献度の選択肢ごとの説明。並びはボタン（`CONTRIBUTION_VALUES`）と一致させる。 @graph-connects none */
const CONTRIBUTION_LEGEND: readonly (readonly [ContributionValue, string])[] = [
  ['対象外', 'デザイナーが貢献する余地はなかった'],
  ['1', '貢献度は少なかった（90〜％）'],
  ['2', 'もう少しで期待値どおりだった（95〜％）'],
  ['3', '期待値と同等の動きだった（100％）'],
  ['4', '期待値を少し超える貢献だった（〜105％）'],
  ['5', '期待値を大きく超える貢献だった（〜110％）'],
];

/** 貢献度で Good コメントを促す値。 @graph-connects none */
const GOOD_TRIGGER_VALUES: readonly ContributionValue[] = ['4', '5'];

/** 「対象外」は尺度の外側なのでバーの目盛には入れない。 @graph-connects none */
const CONTRIBUTION_OFF_SCALE: ContributionValue = '対象外';

/** バーの目盛になる貢献度（1〜5）。 @graph-connects none */
const CONTRIBUTION_SCALE: readonly ContributionValue[] = CONTRIBUTION_VALUES.filter(
  (value) => value !== CONTRIBUTION_OFF_SCALE,
);

/**
 * セクションの地色は **ID 看板の 2 つの radar カードと同じ色**（解像度=青緑 / 貢献度=橙）。
 *
 * 貢献度を桃にしてはいけない。桃は軸色（デザイン実装）として地の上に乗るので、
 * 地と中身が同じ色相になって軸が沈む。さらに桃の地に緑（設計の軸色）が乗ると
 * 色相環でほぼ真反対の組み合わせになり、地と中身が喧嘩して読みにくくなる。
 * 橙なら黄・桃と類似色、緑とも補色関係にならないので 3 軸すべてが地から浮く。
 * @graph-connects none
 */
const RESOLUTION_COLOR = RESOLUTION_RADAR_COLOR;

/** 貢献度セクションの基調色（ID 看板の チーム貢献度評価 radar と同じ橙）。 @graph-connects none */
const CONTRIBUTION_COLOR = EVALUATION_RADAR_COLOR;

/** ID 看板「チーム貢献度評価」の軸色を引く。 @graph-connects none */
function evaluationAxisColor(key: EvaluationAxisKey): string {
  return EVALUATION_AXES.find((axis) => axis.key === key)?.color ?? 'var(--color-accent-primary)';
}

/**
 * 貢献度 3 軸の代表色。ID 看板と同じ 企画（ラフ）=黄 / 設計=緑 / デザイン実装=桃。
 * @graph-connects none
 */
const CONTRIBUTION_AXIS_COLORS: Record<(typeof CONTRIBUTION_AXES)[number], string> = {
  '企画（ラフ）': evaluationAxisColor('planning'),
  設計: evaluationAxisColor('design'),
  デザイン実装: evaluationAxisColor('visual'),
};

/**
 * セクションの色付けは **地ではなく左の縁 + 見出しの文字色**で行う。
 *
 * 地に色を入れると、地の色相と中身の軸色（黄 / 緑 / 桃）が必ずどこかで衝突する
 * （桃の地に緑が乗る等）。色を縁と見出しへ寄せれば、面としての主張は中性のまま
 * 「どのセクションか」だけが色で分かる。主役は目盛バーの軸色。
 * @graph-connects none
 */
function sectionAccent(color: string) {
  return { borderLeftColor: color };
}

/**
 * 見出しの文字色。素の軸色はライトテーマの白地で 4.5:1 を取れない（橙 #f97316 で約 2.9:1）ので、
 * **本文色と混ぜて**使う。`--color-text-primary` はダークで白寄り / ライトで黒寄りなので、
 * 同じ式のままダークでは明るく・ライトでは暗く転び、両テーマでコントラストが確保される。
 * @graph-connects none
 */
function sectionHeadingColor(color: string): string {
  return `color-mix(in srgb, ${color} 55%, var(--color-text-primary))`;
}

/** 選択肢の凡例行。渡された配列の順序どおりに出す（ボタンと同順）。 @graph-connects none */
function Legend({ legend }: { legend: readonly (readonly [string, string])[] }) {
  return (
    <p className="mb-3 text-xs leading-relaxed text-[var(--color-text-muted)]">
      {legend.map(([key, description]) => (
        <span key={key} className="mr-3 inline-block whitespace-nowrap">
          <span className="font-medium text-[var(--color-text-secondary)]">{key}</span>:{' '}
          {description}
        </span>
      ))}
    </p>
  );
}

/**
 * Good / More のコメント欄。見出しは pill ラベルで出す（Good=緑 / More=橙）。
 * 「どちらを書く欄か」が語より色で先に伝わるほうが、選択直後の流れを切らない。
 * @graph-connects none
 */
function CommentField({
  id,
  label,
  color,
  placeholder,
  recommended,
  value,
  onChange,
}: {
  id: string;
  label: string;
  color: string;
  placeholder: string;
  recommended: boolean;
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <div>
      <label htmlFor={id} className="mb-1 flex items-center gap-2">
        <span
          className="rounded-full px-2.5 py-0.5 text-xs font-semibold"
          style={{ background: `color-mix(in srgb, ${color} 18%, transparent)`, color }}
        >
          {label}
        </span>
        {recommended && (
          <span className="text-xs text-[var(--color-accent-primary)]">入力をおすすめします</span>
        )}
      </label>
      <textarea
        id={id}
        rows={3}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        placeholder={placeholder}
        className="w-full rounded border border-[var(--color-border)] bg-[var(--color-bg-base)] p-2 text-sm"
      />
    </div>
  );
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business アンケート回答モーダル本体。未回答タスクを 1 件ずつ回答し、送信後に次件へ進む
 * @graph-connects useSubmitEvaluationAnswer [calls] 回答登録ミューテーション
 * @graph-connects SurveyScaleBar [calls] 解像度・貢献度の目盛バー
 */
export function EvaluationSurveyModal({ tasks, open, onClose }: EvaluationSurveyModalProps) {
  const [index, setIndex] = useState(0);
  const [resolution, setResolution] = useState<(ResolutionValue | null)[]>(
    RESOLUTION_AXES.map(() => null),
  );
  const [contribution, setContribution] = useState<(ContributionValue | null)[]>(
    CONTRIBUTION_AXES.map(() => null),
  );
  const [good, setGood] = useState('');
  const [more, setMore] = useState('');
  const [confirming, setConfirming] = useState(false);

  const submit = useSubmitEvaluationAnswer();
  const task = tasks[index];

  /** 次の未回答へ進む（最後なら閉じる）。入力状態は毎回リセットする。 */
  const goNext = () => {
    setResolution(RESOLUTION_AXES.map(() => null));
    setContribution(CONTRIBUTION_AXES.map(() => null));
    setGood('');
    setMore('');
    setConfirming(false);
    submit.reset();
    if (index + 1 < tasks.length) {
      setIndex(index + 1);
    } else {
      setIndex(0);
      onClose();
    }
  };

  if (!task) return null;

  const resolutionFilled = resolution.filter((v) => v !== null).length;
  const contributionFilled = contribution.filter((v) => v !== null).length;
  const complete =
    resolutionFilled === RESOLUTION_AXES.length && contributionFilled === CONTRIBUTION_AXES.length;
  // 貢献度で 4/5 を選んだら Good を、3 以下・対象外なら More を促す（必須にはしない）。
  const goodRecommended = contribution.some((v) => v !== null && GOOD_TRIGGER_VALUES.includes(v));
  const moreRecommended = contribution.some((v) => v !== null && !GOOD_TRIGGER_VALUES.includes(v));

  const handleSubmit = () => {
    if (!complete) return;
    submit.mutate(
      {
        childIssueKey: task.childIssueKey,
        resolution: resolution as ResolutionAnswer,
        contribution: contribution as ContributionAnswer,
        good: good.trim() === '' ? null : good.trim(),
        more: more.trim() === '' ? null : more.trim(),
      },
      { onSuccess: goNext },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="full"
      title={
        <div className="flex items-center gap-2">
          <span>デザイナー貢献度アンケート</span>
          {tasks.length > 1 && (
            <Badge variant="info" size="sm">
              {index + 1}/{tasks.length} 件
            </Badge>
          )}
        </div>
      }
      footer={
        <div className="flex w-full flex-col gap-2">
          <p className="text-xs text-[var(--color-warning)]">
            ⚠ 回答は 1 件につき 1 回のみです。送信後は編集できません。
          </p>
          {submit.isError && (
            <p className="text-xs text-[var(--color-error)]">
              送信に失敗しました。時間をおいて再度お試しください。
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose} disabled={submit.isPending}>
              キャンセル
            </Button>
            {confirming ? (
              <Button variant="primary" onClick={handleSubmit} disabled={submit.isPending}>
                {submit.isPending ? '送信中…' : 'この内容で送信する'}
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={() => {
                  setConfirming(true);
                }}
                disabled={!complete}
              >
                送信する
              </Button>
            )}
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <section className="rounded border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-3">
          <p className="font-medium text-[var(--color-text-primary)]">{task.childSummary}</p>
          <p className="mt-1 text-xs text-[var(--color-text-muted)]">
            {task.childIssueKey} ／ デザイナー {task.designer} ／ {task.category} ／ {task.pt} pt ／
            対象月度 {task.month}
          </p>
        </section>

        <section
          className="rounded border border-l-[3px] border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-3"
          style={sectionAccent(RESOLUTION_COLOR)}
        >
          <h3
            className="mb-1 flex items-center gap-2 text-sm font-bold"
            style={{ color: sectionHeadingColor(RESOLUTION_COLOR) }}
          >
            案件開始当初の解像度
            <Badge
              variant={resolutionFilled === RESOLUTION_AXES.length ? 'success' : 'warning'}
              size="sm"
            >
              必須 {resolutionFilled}/{RESOLUTION_AXES.length}
            </Badge>
          </h3>
          <Legend legend={RESOLUTION_LEGEND} />
          <div className="space-y-2.5">
            {RESOLUTION_AXES.map((axis, i) => (
              <div
                key={axis}
                className="grid gap-1 sm:grid-cols-[minmax(0,1fr)_28rem] sm:items-center sm:gap-4"
              >
                <span className="text-sm text-[var(--color-text-secondary)]">{axis}</span>
                <SurveyScaleBar
                  scale={RESOLUTION_VALUES}
                  value={resolution[i] ?? null}
                  ariaLabel={axis}
                  color={RESOLUTION_COLOR}
                  onChange={(next) => {
                    setResolution(resolution.map((v, j) => (i === j ? next : v)));
                  }}
                />
              </div>
            ))}
          </div>
        </section>

        <section
          className="rounded border border-l-[3px] border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-3"
          style={sectionAccent(CONTRIBUTION_COLOR)}
        >
          <h3
            className="mb-1 flex items-center gap-2 text-sm font-bold"
            style={{ color: sectionHeadingColor(CONTRIBUTION_COLOR) }}
          >
            フェーズ別 貢献度
            <Badge
              variant={contributionFilled === CONTRIBUTION_AXES.length ? 'success' : 'warning'}
              size="sm"
            >
              必須 {contributionFilled}/{CONTRIBUTION_AXES.length}
            </Badge>
          </h3>
          <Legend legend={CONTRIBUTION_LEGEND} />
          <div className="space-y-2.5">
            {CONTRIBUTION_AXES.map((axis, i) => (
              <div
                key={axis}
                className="grid gap-1 sm:grid-cols-[minmax(0,1fr)_28rem] sm:items-center sm:gap-4"
              >
                <span className="flex items-center gap-1.5 text-sm text-[var(--color-text-secondary)]">
                  <span
                    className="h-2 w-2 shrink-0 rounded-full"
                    style={{ background: CONTRIBUTION_AXIS_COLORS[axis] }}
                  />
                  {axis}
                </span>
                <SurveyScaleBar
                  scale={CONTRIBUTION_SCALE}
                  offScale={CONTRIBUTION_OFF_SCALE}
                  value={contribution[i] ?? null}
                  ariaLabel={axis}
                  color={CONTRIBUTION_AXIS_COLORS[axis]}
                  onChange={(next) => {
                    setContribution(contribution.map((v, j) => (i === j ? next : v)));
                  }}
                />
              </div>
            ))}
          </div>
        </section>

        <section className="grid gap-3 sm:grid-cols-2">
          <CommentField
            id="survey-good"
            label="Good"
            color="var(--color-success)"
            placeholder="Goodだと感じた点"
            recommended={goodRecommended}
            value={good}
            onChange={setGood}
          />
          <CommentField
            id="survey-more"
            label="More"
            color="var(--color-warning)"
            placeholder="理由や Moreだと感じた点"
            recommended={moreRecommended}
            value={more}
            onChange={setMore}
          />
        </section>
      </div>
    </Modal>
  );
}
